import React from 'react';
import TranslationNamespaces from '../../locales/TranslationNamespaces';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { FileCopy } from '@mui/icons-material';
import { QContentBox, QIconButton, QSpinner, useQAlert } from '@kubiq-base/core';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../state/ApiContext';
import CenteredQButton from '../Shared/CenteredQButton';
import HeaderWithDescription from '../Shared/HeaderWithDescription';
import FlexContainer from '../Shared/FlexContainers/FlexContainer';
import PageError from '../Shared/PageError';
import SiteHelper from '../../helpers/SiteHelper';
import { useCache } from '../../state/CachingContext';
import { useAppContext } from '../../state/AppContext';

const RecoveryCodes = (): JSX.Element => {
  const namespace = TranslationNamespaces.twoFA;
  const { t } = useTranslation(namespace);
  const { setEmail } = useAppContext();
  const { getRequest, postRequest } = useApi();
  const { enqueueAlert } = useQAlert();
  const { getCache, setCache, clearCache } = useCache();
  const navigate = useNavigate();

  const [showErrorMessage, setShowErrorMessage] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [codes, setCodes] = React.useState<string[]>([]);

  const redirect = (): void => {
    SiteHelper.handleRedirectExternal(getCache(), getCache('authData'), setEmail, clearCache, setCache, navigate);
  };

  const bootstrapAsync = async (): Promise<void> => {
    await postRequest('TwoFactorAuthentication/ResetRecoveryCodes', {}, setIsSubmitting);

    await getRequest<{ data: React.SetStateAction<string[]> }>(
      'TwoFactorAuthentication/GetRecoveryCodes',
      undefined,
      setIsSubmitting,
    )
      .then((result) => {
        if (result.data) setCodes(result.data);
      })
      .catch(() => setShowErrorMessage(true));
  };

  React.useEffect(() => {
    bootstrapAsync();
  }, []);

  return (
    <>
      {showErrorMessage ? (
        <PageError code='500' message='wentWrong' />
      ) : (
        <>
          <HeaderWithDescription
            headerSuffix='Congratulations'
            description='recoveryCodesDescription'
            translationNamespace={namespace}
          />
          <FlexContainer>
            {isSubmitting ? (
              <QSpinner />
            ) : (
              <QContentBox style={{ content: { position: 'relative' } }}>
                <div
                  id='theCodes'
                  style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', position: 'relative' }}
                >
                  {codes.map((code: string) => (
                    <Box
                      key={code}
                      sx={{
                        color: 'text.secondary',
                        width: 100,
                        display: 'flex',
                        justifyContent: 'center',
                        fontSize: 16,
                        p: 0.5,
                      }}
                    >
                      <Typography variant='overline' fontSize={16}>
                        {code}
                      </Typography>
                    </Box>
                  ))}
                </div>
                {navigator.clipboard && (
                  <Box sx={{ position: 'absolute', bottom: 0, right: 0, mr: 1, mb: 1 }}>
                    <QIconButton
                      onClick={() => {
                        navigator.clipboard.writeText(codes.join('\r\n'));
                        enqueueAlert(t('copiedCodesToClipboard'), { type: 'success', autoHideDuration: 5000 });
                      }}
                    >
                      <FileCopy />
                    </QIconButton>
                  </Box>
                )}
              </QContentBox>
            )}
          </FlexContainer>

          <FlexContainer>
            <CenteredQButton onClick={() => redirect()} color='secondary' fullWidth>
              {t('login')}
            </CenteredQButton>
          </FlexContainer>
        </>
      )}
    </>
  );
};

export default RecoveryCodes;
